import React, { useEffect, useRef, useState } from 'react';

const StreetView = ({ lat, lng }) => {
  const streetViewRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [hasStreetView, setHasStreetView] = useState(false);

  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      setStatus('ready');
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');

    if (existingScript) {
      // Wait for existing script to load
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkInterval);
          setStatus('ready');
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }

    // Load script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDHQYlthhHd4om0fCBMqBynDUAPbJMJJm8`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setTimeout(() => {
        if (window.google && window.google.maps) {
          setStatus('ready');
        } else {
          setError('API loaded but not initialized');
          setStatus('error');
        }
      }, 500);
    };

    script.onerror = () => {
      setError('Failed to load script');
      setStatus('error');
    };

    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (status !== 'ready' || !streetViewRef.current || !window.google || !window.google.maps) return;

    console.log('🎯 Creating Street View at:', lat, lng);

    try {
      // First, check if Street View is available at this location
      const streetViewService = new window.google.maps.StreetViewService();

      streetViewService.getPanorama(
        {
          location: { lat: Number(lat), lng: Number(lng) },
          radius: 50, // Search radius in meters
          source: window.google.maps.StreetViewSource.OUTDOOR
        },
        (data, status) => {
          console.log('📍 Street View availability check:', status);

          if (status === window.google.maps.StreetViewStatus.OK) {
            console.log('✅ Street View available!');
            setHasStreetView(true);

            // Create the panorama
            const panorama = new window.google.maps.StreetViewPanorama(
              streetViewRef.current,
              {
                position: { lat: Number(lat), lng: Number(lng) },
                pov: { heading: 165, pitch: 0 },
                zoom: 1,
                addressControl: false,
                showRoadLabels: false,
                zoomControl: true,
                fullscreenControl: true,
                motionTracking: false
              }
            );

            // Add event listeners for debugging
            panorama.addListener('status_changed', () => {
              console.log('🔄 Panorama status:', panorama.getStatus());
            });

            panorama.addListener('pano_changed', () => {
              console.log('📸 Pano ID:', panorama.getPano());
            });

            // Force a redraw
            setTimeout(() => {
              if (streetViewRef.current) {
                streetViewRef.current.style.backgroundColor = 'transparent';
              }
            }, 1000);

          } else {
            console.warn('❌ No Street View at this location. Status:', status);
            setHasStreetView(false);
            setError(`No Street View imagery available at these coordinates (Status: ${status})`);

            // Show fallback map instead
            const map = new window.google.maps.Map(streetViewRef.current, {
              center: { lat: Number(lat), lng: Number(lng) },
              zoom: 15,
              mapTypeId: 'satellite'
            });

            new window.google.maps.Marker({
              position: { lat: Number(lat), lng: Number(lng) },
              map: map,
              title: 'Location (No Street View)'
            });
          }
        }
      );

    } catch (err) {
      console.error('🔥 Error:', err);
      setError(`Error: ${err.message}`);
    }
  }, [status, lat, lng]);

  // Debug information
  console.log('📊 Status:', status);
  console.log('📊 Has Street View:', hasStreetView);
  console.log('📊 Error:', error);

  if (error) {
    return (
      <div style={{
        width: '100%',
        height: '75vh',
        backgroundColor: '#fff3cd',
        border: '2px dashed #ffc107',
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        <h3 style={{ color: '#856404', marginBottom: '10px' }}>⚠️ Street View Issue</h3>
        <p style={{ color: '#856404', marginBottom: '20px' }}>{error}</p>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '15px',
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          <p><strong>Coordinates tested:</strong> {lat}, {lng}</p>
          <p><strong>Status:</strong> {status}</p>
          <p><strong>Google Maps loaded:</strong> {window.google ? 'Yes' : 'No'}</p>
        </div>
      </div>
    );
  }

  if (status !== 'ready') {
    return (
      <div style={{
        width: '100%',
        height: '75vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '6px solid #e9ecef',
          borderTop: '6px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <p style={{ color: '#6c757d', fontSize: '16px' }}>Loading Street View...</p>
        <p style={{ color: '#adb5bd', fontSize: '12px', marginTop: '10px' }}>
          Checking coordinates: {lat}, {lng}
        </p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '75vh',
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }}>
      <div
        ref={streetViewRef}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: hasStreetView ? 'transparent' : '#f8f9fa'
        }}
      />

      {hasStreetView && (
        <div style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '5px 10px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 1000
        }}>
          🏙️ Street View Active • Drag to look around
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default StreetView;